import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import client from "../api";
import { useI18n, formatApiErrorDetail } from "../i18n";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Logo from "../components/Logo";
import { KeyRound, Check } from "lucide-react";

export default function ResetPassword() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (password.length < 6) { setErr(t.auth.passwordTooShort); return; }
    if (password !== confirm) { setErr(t.auth.passwordsDontMatch); return; }
    setLoading(true);
    try {
      await client.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2200);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || t.auth.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-deep flex flex-col">
      <Nav variant="marketing" />
      <div className="flex-1 max-w-md w-full mx-auto px-6 pt-12 pb-24" data-testid="reset-page">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3"><Logo size="lg" linked={false} /></div>
          <h1 className="font-heading text-3xl tracking-tight">{t.auth.resetTitle}</h1>
          <p className="text-zinc-400 mt-2 text-sm">{t.auth.resetSub}</p>
        </div>

        {!token && (
          <div className="card-neon p-7 text-center" data-testid="reset-missing-token">
            <p className="text-sm text-red-400">{t.auth.missingToken}</p>
            <Link to="/forgot-password" className="btn-outline mt-5 text-xs">{t.auth.forgotCta}</Link>
          </div>
        )}

        {token && !done && (
          <form onSubmit={submit} className="card-neon p-7 space-y-4">
            <div>
              <label className="input-label">{t.auth.newPassword}</label>
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="input-neon" data-testid="reset-password" />
            </div>
            <div>
              <label className="input-label">{t.auth.confirmPassword}</label>
              <input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="input-neon" data-testid="reset-confirm" />
            </div>
            {err && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="reset-error">{err}</div>}
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="reset-submit">
              <KeyRound className="w-4 h-4" />
              {loading ? t.auth.processing : t.auth.resetCta}
            </button>
          </form>
        )}

        {done && (
          <div className="card-neon p-7 text-center glow-cyan" data-testid="reset-done">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-neon-cyan text-[#06061a]">
              <Check className="w-7 h-7" />
            </div>
            <h2 className="font-heading text-xl mt-4">{t.auth.resetDoneTitle}</h2>
            <p className="text-sm text-zinc-400 mt-2">{t.auth.resetDoneSub}</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
